import { useState, useCallback, useMemo } from "react";
import { SalesTransaction } from "../types";
import { classifyOutcome } from "../lib/outcomeBuckets";
import { isSelfSet } from "../utils/selfSet";

// =============================================================================
// useTransactionFilters
// =============================================================================
// PURPOSE: Click-to-filter state machine for the Transaction Log table.
//
// FILTER MODEL:
// - Each column maintains a Set<string> of *included* values
// - No entry in the Map = no filter for that column (all values pass)
// - Left-click  (isolate): set included = Set([clickedValue])
// - Right-click (exclude): remove clickedValue from included set
// - Chip click  (add back): re-add value; if included === all, clear the filter
// - Columns combine with AND; values within a column combine with OR
//
// STATE DESIGN:
// - State stores ONLY includedValues per column (Map<column, Set<string>>)
// - allValues is DERIVED from transactions (never stored) — automatically fresh
//   when background fetch loads more rows, no useEffect sync needed
// =============================================================================

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export type FilterableColumn =
  | "date"
  | "closer"
  | "setter"
  | "warmth"
  | "country"
  | "outcome"
  | "bucket"
  | "program"
  | "cash"
  | "revenue";

export type FilterMode = "isolate" | "exclude";

export interface ColumnFilter {
  column: FilterableColumn;
  includedValues: Set<string>;
  allValues: Set<string>;
}

export interface FilterChip {
  column: FilterableColumn;
  value: string;
}

export interface UseTransactionFiltersResult {
  // State
  filters: Map<FilterableColumn, ColumnFilter>;
  activeFilterCount: number;

  // Actions
  applyFilter: (column: FilterableColumn, value: string, mode: FilterMode) => void;
  removeFilter: (column: FilterableColumn, value: string) => void;
  setColumnValues: (column: FilterableColumn, values: string[]) => void;
  clearColumnFilter: (column: FilterableColumn) => void;
  clearAllFilters: () => void;

  // Derived
  getFilteredData: (
    transactions: SalesTransaction[],
    options?: { exclude?: FilterableColumn }
  ) => SalesTransaction[];
  getChips: () => FilterChip[];
}

// ---------------------------------------------------------------------------
// Value extractor — maps a SalesTransaction to a string for a given column
// ---------------------------------------------------------------------------

export function getColumnValue(t: SalesTransaction, column: FilterableColumn): string {
  switch (column) {
    case "date": {
      // Normalize to YYYY-MM-DD so filtering groups by day, not by timestamp
      const raw = t.dateOfCall || t.timestamp || "";
      return raw ? raw.split("T")[0] : "";
    }
    case "closer":
      return t.closerName || "Unassigned";
    case "setter":
      // Self-set rows are relabelled at the SOURCE of truth, not just in the cell, so
      // the displayed text and `data-filter-value` can never disagree. This also fixes a
      // pre-existing split: the same "closer sourced it themselves" state arrives as
      // `"No Setter"` on rows since the 2026-05-16 form change and as NULL→"Unassigned"
      // on older ones, so it was TWO filter values for one concept — clicking either
      // isolated ~half the self-set deals. Now it's one. See utils/selfSet.ts.
      return isSelfSet(t) ? "Self-Set" : t.setterName || "Unassigned";
    case "warmth":
      return t.warmthLevel || "—";
    case "country":
      // Derived from the lead's phone via get_lead_country_for_txns. Null until enrichment
      // lands → "Unknown", the same bucket lead_country_from_phone assigns no-'+' phones, so
      // an isolate groups genuinely-unknown and not-yet-resolved rows under one honest label.
      return t.country || "Unknown";
    case "outcome":
      return t.callOutcome || "";
    case "bucket":
      // Outcome grouped into its display bucket (closed/installment/lost/noshow/…).
      // Powers the summary-strip numbers as filter buttons: one bucket = one
      // value, so the existing single-value isolate/exclude machinery just works.
      // Coexists with the raw `outcome` column the table cells filter on.
      return classifyOutcome(t.callOutcome || "");
    case "program":
      return t.programSold || "—";
    case "cash":
      return String(t.cashCollected);
    case "revenue":
      return String(t.revenueClosed);
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Helper — compute all unique values for a column from a transaction list
// ---------------------------------------------------------------------------

function computeAllValues(transactions: SalesTransaction[], column: FilterableColumn): Set<string> {
  const values = new Set<string>();
  for (const t of transactions) {
    values.add(getColumnValue(t, column));
  }
  return values;
}

// ---------------------------------------------------------------------------
// Helper — check if includedValues covers all of allValues (filter is no-op)
// ---------------------------------------------------------------------------

function isRedundantSet(includedValues: Set<string>, allValues: Set<string>): boolean {
  if (allValues.size === 0) return true;
  if (includedValues.size < allValues.size) return false;
  for (const v of allValues) {
    if (!includedValues.has(v)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTransactionFilters(
  transactions: SalesTransaction[]
): UseTransactionFiltersResult {
  // State: only the included values per column. allValues is always derived.
  const [includedMap, setIncludedMap] = useState<Map<FilterableColumn, Set<string>>>(new Map());

  // -------------------------------------------------------------------------
  // Derive full ColumnFilter objects by combining stored includedValues
  // with freshly computed allValues from the current transactions list.
  // Automatically prunes filters that became redundant after new data loaded.
  // -------------------------------------------------------------------------
  const filters = useMemo((): Map<FilterableColumn, ColumnFilter> => {
    const result = new Map<FilterableColumn, ColumnFilter>();
    for (const [column, includedValues] of includedMap) {
      const allValues = computeAllValues(transactions, column);
      if (!isRedundantSet(includedValues, allValues)) {
        result.set(column, { column, includedValues, allValues });
      }
    }
    return result;
  }, [includedMap, transactions]);

  // -------------------------------------------------------------------------
  // applyFilter — isolate or exclude a value on a column
  // -------------------------------------------------------------------------
  const applyFilter = useCallback(
    (column: FilterableColumn, value: string, mode: FilterMode) => {
      setIncludedMap((prev) => {
        const next = new Map(prev);

        if (mode === "isolate") {
          // Toggle: if already isolated to this exact value, clear the filter
          const existing = prev.get(column);
          if (existing && existing.size === 1 && existing.has(value)) {
            next.delete(column);
          } else {
            next.set(column, new Set([value]));
          }
        } else {
          // mode === "exclude"
          const existing = prev.get(column);
          const allValues = computeAllValues(transactions, column);

          const includedValues = existing ? new Set(existing) : new Set(allValues);
          includedValues.delete(value);

          if (includedValues.size === 0) {
            // Keep with empty set — caller sees zero results (valid edge case)
            next.set(column, includedValues);
          } else if (isRedundantSet(includedValues, allValues)) {
            next.delete(column);
          } else {
            next.set(column, includedValues);
          }
        }

        return next;
      });
    },
    [transactions]
  );

  // -------------------------------------------------------------------------
  // setColumnValues — set the EXACT included set for a column in one shot.
  // Powers the multi-select Country flyout (checkbox picker), where the user
  // chooses N values at once rather than isolating/excluding one at a time.
  // Empty OR covers-all => no filter (the column shows everything), so the
  // flyout's "All" and its zero-checked transient both mean "show all".
  // -------------------------------------------------------------------------
  const setColumnValues = useCallback(
    (column: FilterableColumn, values: string[]) => {
      setIncludedMap((prev) => {
        const next = new Map(prev);
        const allValues = computeAllValues(transactions, column);
        const included = new Set(values);
        if (included.size === 0 || isRedundantSet(included, allValues)) {
          next.delete(column);
        } else {
          next.set(column, included);
        }
        return next;
      });
    },
    [transactions]
  );

  // -------------------------------------------------------------------------
  // removeFilter (chip click) — re-add a value to the column's included set
  // -------------------------------------------------------------------------
  const removeFilter = useCallback(
    (column: FilterableColumn, value: string) => {
      setIncludedMap((prev) => {
        const existing = prev.get(column);
        if (!existing) return prev;

        const next = new Map(prev);
        const includedValues = new Set(existing);
        includedValues.add(value);

        const allValues = computeAllValues(transactions, column);
        if (isRedundantSet(includedValues, allValues)) {
          next.delete(column);
        } else {
          next.set(column, includedValues);
        }

        return next;
      });
    },
    [transactions]
  );

  // -------------------------------------------------------------------------
  // clearColumnFilter — remove the entire filter for a column
  // -------------------------------------------------------------------------
  const clearColumnFilter = useCallback((column: FilterableColumn) => {
    setIncludedMap((prev) => {
      if (!prev.has(column)) return prev;
      const next = new Map(prev);
      next.delete(column);
      return next;
    });
  }, []);

  // -------------------------------------------------------------------------
  // clearAllFilters
  // -------------------------------------------------------------------------
  const clearAllFilters = useCallback(() => {
    setIncludedMap(new Map());
  }, []);

  // -------------------------------------------------------------------------
  // activeFilterCount
  // -------------------------------------------------------------------------
  const activeFilterCount = filters.size;

  // -------------------------------------------------------------------------
  // getFilteredData — apply all active column filters with AND logic
  // -------------------------------------------------------------------------
  const getFilteredData = useCallback(
    (txns: SalesTransaction[], options?: { exclude?: FilterableColumn }): SalesTransaction[] => {
      if (filters.size === 0) return txns;

      return txns.filter((t) => {
        for (const [column, filter] of filters) {
          // Skip the excluded column so a facet can count itself against the
          // OTHER active filters (e.g. the country flyout shows each country's
          // count within the current outcome/setter filter, but every country
          // still stays selectable). Standard faceted-search behavior.
          if (options?.exclude === column) continue;
          const value = getColumnValue(t, column);
          if (!filter.includedValues.has(value)) {
            return false;
          }
        }
        return true;
      });
    },
    [filters]
  );

  // -------------------------------------------------------------------------
  // getChips — all currently excluded values (allValues - includedValues)
  // -------------------------------------------------------------------------
  const getChips = useCallback((): FilterChip[] => {
    const chips: FilterChip[] = [];

    for (const [column, filter] of filters) {
      for (const value of filter.allValues) {
        if (!filter.includedValues.has(value)) {
          chips.push({ column, value });
        }
      }
    }

    return chips;
  }, [filters]);

  return {
    filters,
    activeFilterCount,
    applyFilter,
    removeFilter,
    setColumnValues,
    clearColumnFilter,
    clearAllFilters,
    getFilteredData,
    getChips,
  };
}
